import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Field,
  Form,
  FormActions,
  FormError,
  Input,
  PageContent,
  PageHeader,
  PageShell,
  SettingsSection,
  Textarea,
} from "@h2j-data-team/ui";
import { createGroupInputSchema, type CreateGroupInput } from "@repo/shared";
import { BackLink } from "@/components/back-link";
import { getErrorMessage } from "@/lib/api-client";
import { useCreateGroup } from "@/features/groups/api";

export const Route = createFileRoute("/_authenticated/sync-groups_/new")({
  staticData: { pageTitle: "breadcrumbs.newSyncGroup", breadcrumb: "breadcrumbs.newGroup" },
  component: NewGroupPage,
});

function NewGroupPage() {
  const { t } = useTranslation("sync-groups");
  const navigate = useNavigate();
  const createMutation = useCreateGroup();

  const form = useForm<CreateGroupInput>({
    resolver: zodResolver(createGroupInputSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const onSubmit = async (data: CreateGroupInput) => {
    try {
      const created = await createMutation.mutateAsync(data);
      navigate({ to: "/sync-groups/$groupId", params: { groupId: created.id } });
    } catch {
      // Error is handled by the mutation
    }
  };

  const { errors } = form.formState;

  return (
    <PageShell
      header={
        <PageHeader
          title={t("new.pageTitle")}
          description={t("new.description")}
          back={
            <BackLink to="/sync-groups" search={{ q: undefined }} label={t("new.backToGroups")} />
          }
        />
      }
    >
      <PageContent>
        <Form
          busy={createMutation.isPending}
          onSubmit={form.handleSubmit(onSubmit)}
          className="max-w-2xl"
        >
          <SettingsSection
            title={t("new.cardTitle")}
            description={t("new.cardDescription")}
            footer={
              <FormActions submitLabel={t("new.create")} submittingLabel={t("new.creating")}>
                <Button asChild variant="outline">
                  <Link to="/sync-groups" search={{ q: undefined }}>
                    {t("new.cancel")}
                  </Link>
                </Button>
              </FormActions>
            }
          >
            <div className="flex flex-col gap-4">
              <Field label={t("new.nameLabel")} error={errors.name?.message}>
                <Input placeholder={t("new.namePlaceholder")} {...form.register("name")} />
              </Field>

              <Field label={t("new.descriptionLabel")} error={errors.description?.message}>
                <Textarea
                  placeholder={t("new.descriptionPlaceholder")}
                  rows={3}
                  {...form.register("description")}
                />
              </Field>

              {createMutation.error && (
                <FormError>{getErrorMessage(createMutation.error)}</FormError>
              )}
            </div>
          </SettingsSection>
        </Form>
      </PageContent>
    </PageShell>
  );
}
